import { create } from 'zustand';

const emptyChild = () => ({
  tempId: Math.random().toString(36).slice(2, 9),
  childId: null,
  name: '',
  age: '',
  gender: 'male',
  height: '',
  weight: '',
  photoBlob: null,
  photoUrl: null,
  medicalNotes: '',
  allergyNotes: '',
  cardUid: '',
  cardStatus: null,
  useCard: false
});

// تحويل سجل طفل قادم من قاعدة البيانات (بحث الهاتف) إلى صيغة نموذج الاستقبال.
// childId هو ما يميّزه: عند التأكيد لا يُنشأ سجل جديد بل تُفتح الجلسة على السجل الموجود.
const childFromExisting = (c) => ({
  ...emptyChild(),
  childId: c._id || c.id,
  name: c.name || '',
  age: c.age == null ? '' : String(c.age),
  gender: c.gender || 'male',
  height: c.height == null ? '' : String(c.height),
  weight: c.weight == null ? '' : String(c.weight),
  photoUrl: c.photoUrl || null,
  medicalNotes: c.medicalNotes || '',
  allergyNotes: c.allergyNotes || ''
});

// السطور المكتوبة يدوياً التي بدأ المستخدم تعبئتها فعلاً (تُحفظ عند الاستيراد).
// أي انحراف عن السطر الفارغ يُعد "لُمس" — حتى صورة أو كارت بلا اسم بعد.
const isTouchedManual = (c) =>
  !c.childId &&
  ((c.name || '').trim() !== '' ||
    String(c.age ?? '').trim() !== '' ||
    !!c.photoBlob ||
    !!c.photoUrl ||
    !!c.useCard ||
    (c.cardUid || '').trim() !== '' ||
    String(c.height ?? '').trim() !== '' ||
    String(c.weight ?? '').trim() !== '' ||
    (c.medicalNotes || '').trim() !== '' ||
    (c.allergyNotes || '').trim() !== '' ||
    c.gender !== 'male');

export const useReceptionForm = create((set, get) => ({
  step: 1,
  // يُرفع عند ضغط "التالي" والنموذج غير مكتمل، ليُظهر الحقول الناقصة باللون الأحمر.
  // يُعاد للصفر تلقائياً عند التنقّل بين الخطوات (next/prev/reset).
  attemptedNext: false,

  guardian: {
    id: null,
    isReturning: false,
    lastVisitAt: null,
    name: '',
    phone: '',
    secondaryPhone: '',
    relation: 'father',
    notes: ''
  },

  children: [emptyChild()],

  // كارت واحد لكل العائلة + عدد المرافقين البالغين عليه.
  sharedCard: {
    enabled: false,
    uid: '',
    status: null,
    companionsCount: 0
  },

  // أطفال ولي الأمر المسجّلون سابقاً (من بحث الهاتف) + حالة نافذة اختيارهم.
  existingChildren: [],
  childPickerOpen: false,

  pricing: {
    type: 'package',
    ruleId: null,
    packageId: null,
    package: null,
    rule: null,
    fixedAmount: 0,
    openAmount: 0,
    estimatedAmount: 0
  },

  payment: {
    method: 'cash',
    cashboxId: null,
    paidAmount: 0
  },

  setStep: (step) => set({ step, attemptedNext: false }),
  next: () => set((s) => ({ step: Math.min(4, s.step + 1), attemptedNext: false })),
  prev: () => set((s) => ({ step: Math.max(1, s.step - 1), attemptedNext: false })),
  markAttempt: () => set({ attemptedNext: true }),

  updateGuardian: (patch) => set((s) => ({ guardian: { ...s.guardian, ...patch } })),
  applyGuardianLookup: (record) =>
    set((s) => {
      const existing = record?.children || [];
      // عند تغيّر ولي الأمر تُسقط الأطفال المستوردة من ولي الأمر السابق،
      // وتبقى السطور اليدوية التي بدأ المستخدم كتابتها.
      const manual = s.children.filter((c) => !c.childId);
      const touched = manual.filter(isTouchedManual);

      let children;
      let childPickerOpen = false;
      if (existing.length === 1 && !existing[0].hasActiveSession) {
        // طفل واحد فقط (وليس داخل القاعة الآن) → يُدخل مباشرة بدون نافذة.
        children = [childFromExisting(existing[0]), ...touched];
      } else {
        if (existing.length > 1) childPickerOpen = true;
        children = touched.length ? touched : manual.slice(0, 1);
      }
      if (!children.length) children = [emptyChild()];

      return {
        guardian: {
          ...s.guardian,
          id: record?._id || record?.id || null,
          isReturning: !!record,
          lastVisitAt: record?.lastVisitAt || record?.updatedAt || null,
          name: record?.name || s.guardian.name,
          relation: record?.relation || s.guardian.relation,
          secondaryPhone: record?.secondaryPhone || s.guardian.secondaryPhone,
          notes: record?.notes || s.guardian.notes
        },
        existingChildren: existing,
        childPickerOpen,
        children
      };
    }),

  setChildPickerOpen: (open) => set({ childPickerOpen: !!open }),

  // إدخال الأطفال المحددين من نافذة الاختيار إلى النموذج (بدون تكرار من سبق إدخاله).
  importExistingChildren: (ids) =>
    set((s) => {
      const byId = new Map(s.existingChildren.map((c) => [String(c._id || c.id), c]));
      const alreadyIn = new Set(s.children.filter((c) => c.childId).map((c) => String(c.childId)));
      const toAdd = ids
        .map(String)
        .filter((id) => byId.has(id) && !alreadyIn.has(id))
        .map((id) => childFromExisting(byId.get(id)));
      const kept = s.children.filter((c) => c.childId || isTouchedManual(c));
      const children = [...toAdd, ...kept];
      return {
        children: children.length ? children : [emptyChild()],
        childPickerOpen: false
      };
    }),

  addChild: () => set((s) => ({ children: [...s.children, emptyChild()] })),
  removeChild: (tempId) =>
    set((s) => ({
      children: s.children.length > 1 ? s.children.filter((c) => c.tempId !== tempId) : s.children
    })),
  updateChild: (tempId, patch) =>
    set((s) => ({
      children: s.children.map((c) => (c.tempId === tempId ? { ...c, ...patch } : c))
    })),

  setPricing: (patch) => set((s) => ({ pricing: { ...s.pricing, ...patch } })),
  setPayment: (patch) => set((s) => ({ payment: { ...s.payment, ...patch } })),
  setSharedCard: (patch) => set((s) => ({ sharedCard: { ...s.sharedCard, ...patch } })),

  reset: () =>
    set({
      step: 1,
      attemptedNext: false,
      guardian: {
        id: null,
        isReturning: false,
        lastVisitAt: null,
        name: '',
        phone: '',
        secondaryPhone: '',
        relation: 'father',
        notes: ''
      },
      children: [emptyChild()],
      sharedCard: { enabled: false, uid: '', status: null, companionsCount: 0 },
      existingChildren: [],
      childPickerOpen: false,
      pricing: {
        type: 'package',
        ruleId: null,
        packageId: null,
        package: null,
        rule: null,
        fixedAmount: 0,
        openAmount: 0,
        estimatedAmount: 0
      },
      payment: { method: 'cash', cashboxId: null, paidAmount: 0 }
    }),

  // selectors
  isStep1Valid: () => {
    const g = get().guardian;
    return /^07[0-9]{9}$/.test(g.phone) && (g.name || '').trim().length >= 2;
  },
  isStep2Valid: () => {
    const cs = get().children;
    if (cs.length === 0) return false;
    return cs.every((c) => {
      if ((c.name || '').trim().length < 2) return false;
      // العمر مطلوب — رفض الفراغ صراحةً (Number('') === 0 يخدع المدى [0,18])
      // وعدد صحيح فقط (Joi في الخادم يرفض الكسور).
      if (String(c.age ?? '').trim() === '') return false;
      const n = Number(c.age);
      return Number.isInteger(n) && n >= 0 && n <= 18;
    });
  },
  isStep3Valid: () => {
    // نوع التسعير يُحدَّد ديناميكياً من الإعدادات العامة (hiddenPricingTypes).
    // كل نوع له شرط تحقق مختلف.
    const p = get().pricing;
    if (p.type === 'package') return !!p.packageId;
    if (p.type === 'hourly')  return !!p.ruleId;
    if (p.type === 'fixed')   return Number(p.fixedAmount) > 0;
    if (p.type === 'open')    return Number(p.openAmount) > 0;
    return false;
  },
  isStep4Valid: () => {
    const { children: cs, sharedCard } = get();
    // كارت عائلة مشترك: كارت واحد صالح يكفي لكل الأطفال.
    if (sharedCard.enabled) {
      const uid = (sharedCard.uid || '').trim();
      return uid.length >= 4 && sharedCard.status === 'available';
    }
    // الكارت اختياري: يُسمح بالمتابعة بدون كارت، ولكن إذا أُدخل كارت يجب أن يكون متاحاً.
    return cs.every((c) => {
      const uid = (c.cardUid || '').trim();
      if (!uid) return true;
      return uid.length >= 4 && c.cardStatus === 'available';
    });
  }
}));
